import { useEffect, useState } from 'react';
import { Save, FileDown, AlertCircle, CheckCircle, FileText } from 'lucide-react';

const API = import.meta.env.VITE_API_URL;

export default function Profile() {
  const [aff, setAff] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pii, setPii] = useState<any>(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [w9Loading, setW9Loading] = useState(false);

  const fetchMe = async () => {
    setLoading(true);
    const res = await fetch(`${API}/affiliate-portal/me`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data: any };
    if (json.success) {
      setAff(json.data);
      setForm({
        display_name: json.data.display_name || '',
        legal_name: json.data.legal_name || '',
        business_name: json.data.business_name || '',
        phone: json.data.phone || '',
        address_line1: json.data.address_line1 || '',
        address_line2: json.data.address_line2 || '',
        city: json.data.city || '',
        state: json.data.state || '',
        postal_code: json.data.postal_code || '',
        country: json.data.country || 'US',
        federal_classification: json.data.federal_classification || '',
        tin_type: json.data.tin_type || '',
        full_tin: '',
        bank_account_holder_name: json.data.bank_account_holder_name || '',
        bank_account_type: json.data.bank_account_type || '',
        full_routing: '',
        full_account: '',
        payment_method_preference: json.data.payment_method_preference || 'check',
      });
    }
    setLoading(false);
  };

  useEffect(() => { fetchMe(); }, []);

  const setField = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const save = async () => {
    setError(null);
    setSuccess(null);
    // A TIN only stores when paired with a TIN type — the backend skips the
    // encrypted write otherwise, which would look like a silent "didn't save".
    if (form.full_tin?.trim() && !form.tin_type) {
      setError('Please select a TIN Type (SSN or EIN) before saving your TIN.');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      full_tin: form.full_tin?.trim() || undefined,
      full_routing: form.full_routing?.trim() || undefined,
      full_account: form.full_account?.trim() || undefined,
    };
    const res = await fetch(`${API}/affiliate-portal/me`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = (await res.json()) as { success: boolean; error?: string };
    setSaving(false);
    if (!json.success) {
      setError(json.error || 'Save failed');
      return;
    }
    setSuccess('Profile saved.');
    setTimeout(() => setSuccess(null), 3000);
    fetchMe();
  };

  const reveal = async () => {
    if (pii) { setPii(null); return; }
    setRevealLoading(true);
    const res = await fetch(`${API}/affiliate-portal/me/pii`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data?: any; error?: string };
    setRevealLoading(false);
    if (!json.success) { setError(json.error || 'Could not reveal PII'); return; }
    setPii(json.data);
    setTimeout(() => setPii(null), 30000);
  };

  const generateW9 = async () => {
    setW9Loading(true);
    setError(null);
    const res = await fetch(`${API}/affiliate-portal/me/w9`, { method: 'POST', credentials: 'include' });
    const json = (await res.json()) as { success: boolean; error?: string };
    setW9Loading(false);
    if (!json.success) {
      setError(json.error || 'W-9 generation failed');
      return;
    }
    setSuccess('W-9 PDF generated. Click "Download W-9" to view.');
    setTimeout(() => setSuccess(null), 5000);
  };

  const downloadW9 = () => {
    window.open(`${API}/affiliate-portal/me/w9`, '_blank');
  };

  if (loading) return <div className="text-center text-slate-500 dark:text-slate-400 py-8">Loading...</div>;
  if (!aff) return <div className="text-center text-slate-500 dark:text-slate-400 py-8">Profile unavailable.</div>;

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">My Profile</h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
        Keep your contact and tax information up to date. Legal name + TIN are required before you can be enrolled for commissions.
      </p>

      {error && <Alert kind="error">{error}</Alert>}
      {success && <Alert kind="success">{success}</Alert>}

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-4">
        <h3 className="font-semibold mb-3">Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Display Name"><Input v={form.display_name} onChange={(v) => setField('display_name', v)} /></Field>
          <Field label="Email (read-only — contact support to change)"><Input v={aff.email} readOnly /></Field>
          <Field label="Phone"><Input v={form.phone} onChange={(v) => setField('phone', v)} /></Field>
          <Field label="Referral Code (yours)"><Input v={aff.referral_code} readOnly /></Field>
        </div>
      </section>

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-4">
        <h3 className="font-semibold mb-3">Address (W-9)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Address line 1"><Input v={form.address_line1} onChange={(v) => setField('address_line1', v)} /></Field>
          <Field label="Address line 2"><Input v={form.address_line2} onChange={(v) => setField('address_line2', v)} /></Field>
          <Field label="City"><Input v={form.city} onChange={(v) => setField('city', v)} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="State"><Input v={form.state} onChange={(v) => setField('state', v)} /></Field>
            <Field label="ZIP"><Input v={form.postal_code} onChange={(v) => setField('postal_code', v)} /></Field>
          </div>
          <Field label="Country"><Input v={form.country} onChange={(v) => setField('country', v)} /></Field>
        </div>
      </section>

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-4">
        <h3 className="font-semibold mb-3">W-9 Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
          <Field label="Legal Name (as on tax return)"><Input v={form.legal_name} onChange={(v) => setField('legal_name', v)} /></Field>
          <Field label="Business Name (if applicable)"><Input v={form.business_name} onChange={(v) => setField('business_name', v)} /></Field>
          <Field label="Federal Classification">
            <Input v={form.federal_classification} onChange={(v) => setField('federal_classification', v)} placeholder="e.g., Individual / S-Corp / LLC" />
          </Field>
          <Field label="TIN Type">
            <select value={form.tin_type} onChange={(e) => setField('tin_type', e.target.value)} className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-3 py-2">
              <option value="">—</option>
              <option value="ssn">SSN</option>
              <option value="ein">EIN</option>
            </select>
          </Field>
          <Field label={aff.tin_last_4 ? `Full TIN (currently set: ••••${aff.tin_last_4})` : 'Full TIN'}>
            <Input
              type="password"
              v={form.full_tin}
              onChange={(v) => setField('full_tin', v)}
              placeholder={aff.tin_last_4 ? '— unchanged —' : '123-45-6789'}
            />
          </Field>
        </div>

        {(aff.tin_last_4) && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
            <div className="flex items-center justify-between">
              <span className="text-amber-900">
                {pii ? <span>⚠ Showing full PII. Auto-hides in 30s.</span> : 'Click to view your full TIN and bank info.'}
              </span>
              <button onClick={reveal} disabled={revealLoading} className="px-3 py-1 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:opacity-50">
                {revealLoading ? 'Loading...' : pii ? 'Hide' : 'Show'}
              </button>
            </div>
            {pii && (
              <div className="mt-3 font-mono text-sm space-y-1">
                <div>TIN: <span className="text-blue-700">{pii.tin || '—'}</span></div>
                <div>Bank routing: <span className="text-blue-700">{pii.bank_routing || '—'}</span></div>
                <div>Bank account: <span className="text-blue-700">{pii.bank_account || '—'}</span></div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-4">
        <h3 className="font-semibold mb-3">Payment Preference</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
          <Field label="Method">
            <select value={form.payment_method_preference} onChange={(e) => setField('payment_method_preference', e.target.value)} className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-3 py-2">
              <option value="check">Check</option>
              <option value="ach_manual">ACH (Manual)</option>
              <option value="wallet_credit">Wallet Credit</option>
            </select>
          </Field>
        </div>
        {(form.payment_method_preference === 'ach_manual') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Bank Account Holder"><Input v={form.bank_account_holder_name} onChange={(v) => setField('bank_account_holder_name', v)} /></Field>
            <Field label="Account Type">
              <select value={form.bank_account_type} onChange={(e) => setField('bank_account_type', e.target.value)} className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-3 py-2">
                <option value="">—</option>
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </Field>
            <Field label={aff.bank_account_last_4 ? `Routing # (account ending ••••${aff.bank_account_last_4})` : 'Routing #'}>
              <Input type="password" v={form.full_routing} onChange={(v) => setField('full_routing', v)} placeholder={aff.bank_account_last_4 ? '— unchanged —' : '9 digits'} />
            </Field>
            <Field label="Account #">
              <Input type="password" v={form.full_account} onChange={(v) => setField('full_account', v)} placeholder={aff.bank_account_last_4 ? '— unchanged —' : '7-12 digits'} />
            </Field>
          </div>
        )}
      </section>

      <div className="flex gap-2 mb-6">
        <button onClick={save} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      {/* W-9 PDF */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6">
        <h3 className="font-semibold mb-2 flex items-center gap-2"><FileText className="w-5 h-5" /> W-9 Document</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
          Generate a PDF of your W-9 information for your records or to send to your accountant. Make sure your legal name and full TIN are saved above first.
        </p>
        <div className="flex gap-2">
          <button onClick={generateW9} disabled={w9Loading || !aff.tin_last_4 || !aff.legal_name} className="px-4 py-2 bg-gray-100 text-slate-700 dark:text-slate-300 rounded hover:bg-gray-200 disabled:opacity-50">
            {w9Loading ? 'Generating...' : 'Generate W-9 PDF'}
          </button>
          <button onClick={downloadW9} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
            <FileDown className="w-4 h-4" /> Download Latest
          </button>
        </div>
        {(!aff.tin_last_4 || !aff.legal_name) && (
          <p className="text-xs text-orange-600 mt-2">Legal name and full TIN must be saved before generating a W-9.</p>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">{label}</label>{children}</div>;
}

function Input({ v, onChange, type = 'text', placeholder = '', readOnly }: { v: string; onChange?: (v: string) => void; type?: string; placeholder?: string; readOnly?: boolean }) {
  return <input type={type} value={v ?? ''} onChange={onChange ? (e) => onChange(e.target.value) : undefined} placeholder={placeholder} readOnly={readOnly} className={`w-full border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-3 py-2 text-sm ${readOnly ? 'bg-gray-50 dark:bg-slate-800' : 'bg-white dark:bg-slate-800'}`} />;
}

function Alert({ kind, children }: { kind: 'error' | 'success'; children: React.ReactNode }) {
  const cls = kind === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200';
  const Icon = kind === 'error' ? AlertCircle : CheckCircle;
  return <div className={`mb-4 p-3 rounded border flex items-start gap-2 ${cls}`}><Icon className="w-5 h-5 mt-0.5" /><span>{children}</span></div>;
}
